// pms-adapters / supabase / cardFindings — C6 Finance's window-1 card findings
// (PMS-LEDGER-L0B-BRIEF, migrations 20261004200100 / 210100 / 220100).
//
// One RPC, `rec_card_findings`. Its three answers are kept apart on purpose:
//   - findings        → rows, rendered;
//   - a stale feed    → ONE marker row (class `gate_closed`), which becomes
//                       `gateClosedReason` and an EMPTY findings list, so the
//                       screen can say « flux en retard » and never « rien à
//                       signaler »;
//   - a denial / fail → an error. A missing `fin.findings.read` is `forbidden`.

import {
  err,
  ok,
  type CardFinding,
  type CardFindingClass,
  type CardFindingState,
  type CardFindings,
  type CardFindingsDay,
  type IsoDate,
  type PropertyCode,
  type Result,
} from '@lemontaigu/pms-core';
import { toPmsError, type SupabaseClient } from './support.js';

/** One row of the RPC, verbatim. */
export interface CardFindingRow {
  class: string;
  state: string;
  subject_date: string;
  payment_id: number | null;
  tap_uuid: string | null;
  amount_cents: number | null;
  reference: string | null;
  client_ref: string | null;
  party_label: string | null;
  tapped_at: string | null;
  written_at: string | null;
  explanation: string;
}

const CLASSES: readonly CardFindingClass[] = ['A', 'H', 'N', 'C', 'B', 'E'];
const STATES: readonly CardFindingState[] = ['awaiting_remise', 'lead', 'overdue'];

/** A row this build cannot read is dropped from the list but NEVER silently:
 *  the caller gets `unreadable` and shows it. Returning fewer findings than the
 *  database holds without saying so is the « all clear » failure. */
export function toCardFindingsDay(rows: CardFindingRow[]): { day: CardFindingsDay; unreadable: number } {
  const marker = rows.find((r) => r.class === 'gate_closed');
  if (marker) {
    return { day: { gateClosedReason: marker.explanation, findings: [] }, unreadable: 0 };
  }
  const findings: CardFinding[] = [];
  let unreadable = 0;
  for (const r of rows) {
    const cls = CLASSES.find((c) => c === r.class);
    const state = STATES.find((s) => s === r.state);
    if (!cls || !state) {
      unreadable += 1;
      continue;
    }
    findings.push({
      class: cls,
      state,
      subjectDate: r.subject_date as IsoDate,
      reference: r.reference,
      clientRef: r.client_ref,
      partyLabel: r.party_label,
      amountCents: r.amount_cents ?? 0,
      tappedAt: r.tapped_at,
      writtenAt: r.written_at,
      explanation: r.explanation,
    });
  }
  return { day: { gateClosedReason: null, findings }, unreadable };
}

export class SupabaseCardFindings implements CardFindings {
  constructor(private readonly db: SupabaseClient) {}

  async day(propertyCode: PropertyCode, day: IsoDate): Promise<Result<CardFindingsDay>> {
    const r = await this.db.rpc('rec_card_findings', { p_property: propertyCode, p_day: day });
    if (r.error) {
      if (r.error.code === '42501') {
        return err('forbidden', 'Accès refusé — contrôles des paiements carte', { cause: r.error });
      }
      return { ok: false, error: toPmsError(r.error) };
    }
    if (!Array.isArray(r.data)) {
      return err('unknown', 'Contrôles carte : réponse illisible', { retryable: false });
    }
    const { day: answer, unreadable } = toCardFindingsDay(r.data as CardFindingRow[]);
    // Rows this build cannot read mean the build is older than the database:
    // refuse, rather than show a shorter list as if it were complete.
    if (unreadable > 0) {
      return err('unknown', 'Contrôles carte : une ligne illisible — recharger la page', { retryable: false });
    }
    return ok(answer);
  }
}
